import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { CheckCircle } from "lucide-react";

const accent = "#D9B8CE";
const green = "#43BD98";

const listVariants = {
	hidden: { opacity: 0 },
	show: { opacity: 1, transition: { staggerChildren: 0.1 } },
};

const itemVariants = {
	hidden: { opacity: 0, x: -15 },
	show: { opacity: 1, x: 0, transition: { duration: 0.35 } },
};

export default function Pricing2() {
	const { t } = useTranslation();

	const planFeatures = t("salon2.pricing.plan.features", {
		returnObjects: true,
	});
	const planProFeatures = t("salon2.pricing.planPro.features", {
		returnObjects: true,
	});

	const plans = [
		{
			id: "complete",
			title: t("salon2.pricing.plan.name"),
			price: t("salon2.pricing.plan.price"),
			period: t("salon2.pricing.plan.period"),
			note: t("salon2.pricing.plan.note"),
			badge: null,
			highlighted: true,
			features: Array.isArray(planFeatures) ? planFeatures : [],
			cta: t("salon2.pricing.plan.cta"),
			trial: t("salon2.pricing.plan.trial"),
		},
		{
			id: "shop",
			title: t("salon2.pricing.planPro.name"),
			price: t("salon2.pricing.planPro.price"),
			period: t("salon2.pricing.planPro.period"),
			note: t("salon2.pricing.planPro.note"),
			badge: t("salon2.pricing.planPro.badge"),
			highlighted: false,
			features: Array.isArray(planProFeatures) ? planProFeatures : [],
			cta: t("salon2.pricing.planPro.cta"),
			trial: t("salon2.pricing.planPro.trial"),
		},
	];

	return (
		<section
			id="precios"
			className="py-24 overflow-hidden"
			style={{
				backgroundColor: "#fff9fc",
				fontFamily: "'Poppins', sans-serif",
			}}
		>
			<div className="max-w-6xl mx-auto px-6 lg:px-16">
				{/* Header */}
				<motion.div
					initial={{ opacity: 0, y: 30 }}
					whileInView={{ opacity: 1, y: 0 }}
					transition={{ duration: 0.7, ease: "easeOut" }}
					viewport={{ once: true }}
					className="text-center mb-14"
				>
					<p
						className="uppercase tracking-wider text-sm font-semibold mb-3"
						style={{ color: green }}
					>
						{t("salon2.pricing.badge")}
					</p>
					<h2
						className="mb-4"
						style={{
							fontSize: "clamp(1.8rem, 5vw, 2.8rem)",
							fontWeight: 800,
							color: "#1a1a1a",
						}}
					>
						{t("salon2.pricing.title")}
					</h2>
					<p
						className="max-w-2xl mx-auto"
						style={{ color: "#666", fontSize: "1.05rem", lineHeight: 1.7 }}
					>
						{t("salon2.pricing.subtitle")}
					</p>
				</motion.div>

				{/* Plans */}
				<div className="flex flex-wrap justify-center gap-8 mb-16">
					{plans.map((plan) => (
						<motion.div
							key={plan.id}
							initial={{ opacity: 0, scale: 0.95 }}
							whileInView={{ opacity: 1, scale: 1 }}
							transition={{ duration: 0.6, ease: "easeOut" }}
							viewport={{ once: true, amount: 0.3 }}
							className="w-full max-w-[440px] rounded-2xl p-8 text-left"
							style={{
								backgroundColor: "#fff",
								boxShadow: plan.highlighted
									? "0 16px 48px rgba(67,189,152,0.15)"
									: "0 12px 40px rgba(0,0,0,0.06)",
								border: plan.highlighted
									? `2px solid ${green}`
									: `1px solid ${accent}33`,
							}}
						>
							{/* Badge */}
							{plan.badge && (
								<span
									className="inline-block text-xs font-semibold px-3 py-1 rounded-full mb-3"
									style={{
										backgroundColor: `${green}15`,
										color: green,
									}}
								>
									{plan.badge}
								</span>
							)}

							{/* Title */}
							<h3
								className="text-xl font-bold mb-1"
								style={{ color: "#222" }}
							>
								{plan.title}
							</h3>

							{/* Price */}
							<div className="flex items-end gap-1 mt-5 mb-1">
								<span
									className="font-extrabold leading-none"
									style={{
										fontSize: "2.6rem",
										color: plan.highlighted ? green : accent,
									}}
								>
									{plan.price}
								</span>
								<span
									className="text-base mb-1"
									style={{ color: "#666" }}
								>
									{plan.period}
								</span>
							</div>
							<p className="text-xs mb-6" style={{ color: "#999" }}>
								{plan.note}
							</p>

							{/* Features */}
							<motion.ul
								variants={listVariants}
								initial="hidden"
								whileInView="show"
								viewport={{ once: true, amount: 0.3 }}
								className="space-y-3 mb-8"
							>
								{plan.features.map((feature, idx) => (
									<motion.li
										key={idx}
										variants={itemVariants}
										className="flex items-start gap-2.5 text-sm leading-relaxed"
									>
										<CheckCircle
											size={18}
											className="flex-shrink-0 mt-0.5"
											style={{
												color: plan.highlighted ? green : accent,
											}}
										/>
										<span style={{ color: "#444" }}>{feature}</span>
									</motion.li>
								))}
							</motion.ul>

							{/* CTA */}
							<motion.a
								href="https://creator.applaby.com?onboarding=true&appType=Services&appSubtype=Salon"
								target="_blank"
								rel="noreferrer"
								whileHover={{ scale: 1.03 }}
								whileTap={{ scale: 0.97 }}
								className="block w-full text-center py-3.5 rounded-xl font-semibold text-white transition-all duration-300"
								style={{
									background: `linear-gradient(135deg, ${green} 0%, #33a884 100%)`,
								}}
							>
								{plan.cta}
							</motion.a>

							{/* Trial note for first plan */}
							{plan.trial && (
								<p
									className="text-center text-xs mt-3"
									style={{ color: "#999" }}
								>
									{plan.trial}
								</p>
							)}
						</motion.div>
					))}
				</div>
			</div>
		</section>
	);
}
